import { Paper } from "@material-ui/core";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import clsx from "clsx";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { t } from "ttag";
import { getDateFnsLocale } from "@utils/dateUtils";
import { Button } from '@coolremote/shared';
import { IDateRange } from "../../models/Selections";
import useStyles from "./CoolDateRangePicker.style";
import { DateRange, DateRangePicker } from "./DateRangePicker";
import { DateRangePicker as MobileDateRangePicker } from "./MobileDateRangePicker";

type IHumanDateRange = "last hour" | "last 24 hours" | "last 7 days" | "last 30 days" | "last year";

export interface ICoolDateRangePickerProps {
  initialRange: IDateRange | IHumanDateRange | null;
  handleSubmit: (range: IDateRange | null) => void;
  options?: ICoolDateRangePickerOptions;
  mobile?: boolean;
  customRange?: string;
  clearCustomRange?: any;
  onlyPastAllowed?: boolean;
  minDate?: any;
  noRangeLimit?: boolean;
  reinit?: boolean;
  hideIfNull?: boolean;
}

export interface ICoolDateRangePickerOptions {
  classNameFor?: string;
}
const defaultCoolDateRangePickerOptions = {};

const getRangeFromHDR = (hdr: string) => {
  const range = { begin: new Date(), end: new Date() };
  switch (hdr) {
    case "last hour":
      range.begin = new Date(new Date().getTime() - 1000 * 60 * 60);
      range.end = new Date();
      break;
    case "last 24 hours":
      range.begin = new Date(new Date().setHours(0, 0, 0) - 24 * 60 * 60 * 1000);
      range.end = new Date();
      break;
    case "last 7 days":
      range.begin = new Date(new Date().setHours(0, 0, 0) - 7 * 24 * 60 * 60 * 1000);
      range.end = new Date();
      break;
    case "last 30 days":
      range.begin = new Date(new Date().setHours(0, 0, 0) - 30 * 24 * 60 * 60 * 1000);
      range.end = new Date();
      break;
    case "last year":
      range.begin = new Date(new Date().setHours(0, 0, 0) - 364 * 24 * 60 * 60 * 1000);
      range.end = new Date();
      break;
  }

  return range;
};

export default function CoolDateRangePicker({
  initialRange,
  handleSubmit,
  options,
  mobile = false,
  customRange = "",
  clearCustomRange,
  onlyPastAllowed,
  minDate,
  noRangeLimit = false,
  reinit = false,
  hideIfNull = false
}: ICoolDateRangePickerProps) {
  const classes = useStyles();
  const [range, setRange] = useState<DateRange>(
    initialRange === null
      ? getRangeFromHDR("last 24 hours")
      : typeof initialRange === "string"
        ? getRangeFromHDR(initialRange)
        : { begin: initialRange.startDate, end: initialRange.endDate }
  );
  const [customizedRange, setCustomRange] = useState<any>(null);

  options = { ...defaultCoolDateRangePickerOptions, ...options };

  useEffect(() => {
    if (!customRange) {
      return;
    }

    const newRange = getRangeFromHDR(customRange);
    setRange(newRange);
    setCustomRange(newRange);

  }, [customRange]);

  const handleSelect = (range: DateRange) => {
    setRange(range);
    if (customRange && clearCustomRange) {
      setCustomRange(null);
      clearCustomRange();
    }
  };

  const isFullRangeSelected = range && range.begin && range.end

  const handleUserSubmit = () => {
    // Make sure the range conver whole day

    handleSubmit({
      startDate: (range && range.begin) ? new Date(new Date(range.begin.getTime()).setHours(0, 0, 0)) :
        new Date(range.begin as Date),
      endDate: (range && range.end) ? new Date(new Date(range.end?.getTime()).setHours(23, 59, 59)) :
        new Date(range.end as Date)
    });
  };

  return (
    <Paper className={clsx(classes.drpContainer, { [classes.drpContainerMobile]: mobile })}>
      <MuiPickersUtilsProvider utils={DateFnsUtils} locale={getDateFnsLocale()}>
        {mobile ?
          <MobileDateRangePicker
            disableFuture
            orientation="portrait"
            variant="static"
            value={range}
            open={true}
            customRange={customizedRange}
            onChange={handleSelect}
            noRangeLimit={noRangeLimit}
            okLabel={t`OK`}
            cancelLabel={t`Cancel`}
            clearLabel={t`Clear`}
            leftArrowButtonProps={{ 'aria-label': t`Previous month` }}
            rightArrowButtonProps={{ 'aria-label': t`Next month` }}
          /> :
          <DateRangePicker
            disableFuture
            orientation="portrait"
            variant="static"
            value={hideIfNull && initialRange === null ? undefined : range}
            open={true}
            minDate={minDate}
            customRange={customizedRange}
            onlyPastAllowed={onlyPastAllowed}
            maxDate={new Date(new Date().setDate(new Date().getDate() + 1))}
            onChange={handleSelect}
            noRangeLimit={noRangeLimit}
            okLabel={t`OK`}
            cancelLabel={t`Cancel`}
            clearLabel={t`Clear`}
            leftArrowButtonProps={{ 'aria-label': t`Previous month` }}
            rightArrowButtonProps={{ 'aria-label': t`Next month` }}
          />}
      </MuiPickersUtilsProvider>
      <div className={mobile ? classes.drpButtonsMobile : classes.drpButtons}>
        <Button marginRight white width={mobile ? 100 : 90} onClick={() => handleSubmit(null)}>{t`CANCEL`}</Button>
        <Button width={mobile ? 100 : 70} disabled={!isFullRangeSelected} onClick={handleUserSubmit}>{mobile ? t`Done` : t`OK`}</Button>
      </div>
    </Paper>
  );
}

// On production:
//
// admin
// Abcd@1234
